import { library } from '@fortawesome/fontawesome-svg-core';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement, useContext } from 'react';
import { useHistory } from 'react-router';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { Settings } from '../../../../modules/settings/SettingsContext';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { Home } from '../../../routes/config/Home';
import { DatasetDetails } from '../DatasetDetails.context';
import { useDatasetPermissions } from '../datasets.permissions';
import { IDatasetDetailsImage } from '../datasetsDetails.store';
import { IDatasetDetailsImagesProps } from './DatasetDetailsImagesList';

library.add(faDownload, faEye);

export const DatasetDetailsImagesListTable = observer((props: IDatasetDetailsImagesProps): ReactElement => {
  const settingsContext = useContext(Settings);
  const { t } = useTranslator();
  const history = useHistory();

  const permissions = useDatasetPermissions();
  const context = useContext(DatasetDetails);
  const canDeleteUnmarkedImages = permissions.canDeleteUnmarkedImages(context.store.details.status, context.store.details.createdById);
  const canDeleteMarkedImages = permissions.canDeleteMarkedImages();
  const canRenameImages = permissions.canRenameImages(context.store.details.status, context.store.details.createdById);
  const canDownloadImages = permissions.canDownloadImages(context.store.details.status, context.store.details.createdById);

  const gColumns = (): IGColumnProps<IDatasetDetailsImage>[] => {
    const columns: IGColumnProps<IDatasetDetailsImage>[] = [
      {
        headerName: '',
        renderer: renderCheckbox,
        headerRenderer: renderSelectHeader,
      },
      {
        sortBy: 'name',
        headerName: t('name'),
        renderer: renderName,
      },
      {
        sortBy: 'createdDate',
        headerName: t('uploadDate'),
        renderer: (image: IDatasetDetailsImage) => <div>{toLocaleDateTimeString(image.createdDate)}</div>,
      },
      {
        sortBy: 'width',
        headerName: t('image_width'),
        renderer: (x) => x.width,
      },
      {
        sortBy: 'height',
        headerName: t('image_height'),
        renderer: (x) => x.height,
      },
      {
        sortBy: 'size',
        headerName: t('image_size'),
        renderer: renderSize,
      },
      {
        sortBy: 'annotationsCount',
        headerName: t('annotated'),
        renderer: (x) => x.annotationsCount,
      },
      {
        sortBy: 'acceptedAnnotationsCount',
        headerName: t('accepted'),
        renderer: (x) => x.acceptedAnnotationsCount,
      },
      {
        headerName: t('actions'),
        renderer: renderActions,
        actions: true,
        width: 'min-content',
      },
    ];

    return columns;
  };

  const onSortChanged = (orderBy: string, orderType: string): void => {
    void props.onOrderingChange(orderBy, orderType);
  };

  const handleDownloadClick = (id: string) => (): void => {
    void props.onImageDownload(id);
  };

  const handleDeleteClick = (id: string) => (): void => {
    props.onImageDeleted(id);
  };

  const handleRenameClick = (id: string) => (): void => {
    props.onRenameImage(id);
  };

  const handlePageSizeChange = (pageNumber: number, pageSize: number): void => {
    settingsContext.setDatasetDetailsImagesPageSize(pageSize);
    void props.onPaginationChange(pageNumber, pageSize);
  };

  const getPreviewUrl = (image: IDatasetDetailsImage): string => {
    const { datasetId, workspaceId } = props;
    return Home.Datasets.Details.Preview.withParams({ workspaceId, datasetId, imageId: image.id });
  };

  const renderCheckbox = (image: IDatasetDetailsImage): ReactElement => (
    <div>
      <S_Checkbox
        checked={props.imagesToBulkDelete.some((x) => x === image.id)}
        onClick={(e): void => {
          props.onThumbCheck(image.id, e.nativeEvent.shiftKey);
        }}
        value={image.id}
        id={image.id}
      />
    </div>
  );

  const renderSelectHeader = (): ReactElement => (
    <S_Checkbox
      checked={props.datasetImages.length === props.imagesToBulkDelete.length}
      onClick={(): void => {
        props.onSelectAll(!(props.datasetImages.length === props.imagesToBulkDelete.length));
      }}
      id={'id-select-all'}
      value={'value-select-all'}
    />
  );

  const renderActions = (image: IDatasetDetailsImage): ReactElement => {
    let title = t('delete_image');
    if (!canDeleteMarkedImages.allowed) {
      if (image.hasLocks) {
        title = t('image_is_already_locked');
      } else if (image.hasAnnotations) {
        title = t('image_is_already_marked');
      }
    }

    return (
      <>
        <IconButton
          size='sm'
          color='primary'
          title={t('preview_image')}
          onClick={() => {
            history.push(getPreviewUrl(image));
          }}
        >
          <FontAwesomeIcon icon={faEye} />
        </IconButton>
        <EnableFor hasPermission={canRenameImages}>
          <IconButton
            size='sm'
            color='primary'
            disabled={!canRenameImages.allowed}
            onClick={handleRenameClick(image.id)}
            title={t('rename_image')}
          >
            <EditIcon />
          </IconButton>
        </EnableFor>
        <EnableFor hasPermission={canDownloadImages}>
          <IconButton
            size='sm'
            color='primary'
            disabled={!props.isImageDownloadable}
            onClick={handleDownloadClick(image.id)}
            title={t(props.isImageDownloadable ? 'download_image' : '')}
          >
            <FontAwesomeIcon icon={faDownload} />
          </IconButton>
        </EnableFor>
        <EnableFor hasPermission={canDeleteUnmarkedImages}>
          <IconButton
            size='sm'
            disabled={!canDeleteMarkedImages.allowed && (image.hasAnnotations || image.hasLocks)}
            title={title}
            onClick={handleDeleteClick(image.id)}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
      </>
    );
  };

  const renderName = (image: IDatasetDetailsImage): ReactElement =>
    props.disabled ? (
      <span>{image.name}</span>
    ) : (
      <S_Link2
        color='text'
        title={image.name}
        to={getPreviewUrl(image)}
      >
        {image.name}
      </S_Link2>
    );

  const renderSize = (image: IDatasetDetailsImage): ReactElement => <span>{image.size.toFixed(2)}</span>;

  return (
    <GTable
      isLoading={props.isLoading}
      columns={gColumns()}
      noItemsPlaceholder={t('no_images_added')}
      items={props.datasetImages}
      onSortChanged={onSortChanged}
      paginationProps={{
        pageNumber: props.paging.pageNumber,
        pageSize: props.paging.pageSize,
        totalCount: props.paging.totalCount,
        onChange: handlePageSizeChange,
      }}
      sortingModel={props.paging.orderBy && props.paging.orderType ? [{ orderBy: props.paging.orderBy, orderType: props.paging.orderType }] : []}
    />
  );
});
